import { AbilityBuilder, createMongoAbility, MongoQuery, RawRuleOf } from '@casl/ability';
import { SelfServiceUserAccount, SelfServiceUserInfoDto } from '@ekt-group/general-purpose-api-interfaces';

import { ALLOWED_TO_ADDITIONAL_DISCHARGES_SUBJECT } from '@/features/additional-discharges';

import { Actions, AppAbility, Subjects } from './types';

// TODO: remove "internal" addition after interfaces get updated
export function defineAbilityFor(
  user: SelfServiceUserInfoDto & {
    internal: { role: string; permissions: Pick<SelfServiceUserAccount['permissions'][0], 'subject' | 'action'>[] }[];
  },
  code?: string,
): AppAbility {
  const { can, build } = new AbilityBuilder<AppAbility>(createMongoAbility);

  if (!user?.roles || !user?.accounts) {
    return build();
  }

  const accountCode = code ?? user.code;
  const account = user.accounts.find((acct) => acct.code === accountCode);

  for (const { role: internalRole, permissions } of user?.internal || []) {
    const additionalDischargeAlgorithmPermission = permissions.find(
      (permission) => permission.subject === ALLOWED_TO_ADDITIONAL_DISCHARGES_SUBJECT,
    );

    // TODO: user from enum after interfaces get updated
    if (['TOITS_roll_Admin', 'Admin'].includes(internalRole)) {
      can('read' as any, ALLOWED_TO_ADDITIONAL_DISCHARGES_SUBJECT as any);
      break;
    }
    if (additionalDischargeAlgorithmPermission) {
      can(additionalDischargeAlgorithmPermission.action as any, additionalDischargeAlgorithmPermission.subject as any);
      break;
    }
  }

  if (!account) {
    console.warn(`Account with code ${accountCode} not found`);
    return build();
  }

  for (const permission of account.permissions) {
    addPermissionRule(can, permission);
  }

  return build();
}

interface SubjectActionPair {
  subject: Subjects;
  action: Actions;
}

export function updateAbilities(
  ability: AppAbility,
  user: SelfServiceUserInfoDto,
  selectedCode: string,
  updates?: SubjectActionPair[],
): void {
  const relevantAccount = user.accounts.find((acct) => acct.code === selectedCode);

  if (!relevantAccount) {
    console.warn(`Account with code ${selectedCode} not found`);
    return;
  }

  if (!updates || updates.length === 0) {
    const { can, build } = new AbilityBuilder<AppAbility>(createMongoAbility);

    for (const permission of relevantAccount.permissions) {
      addPermissionRule(can, permission);
    }

    const newAbility = build();
    ability.update(newAbility.rules);
    return;
  }

  const invalidUpdates: SubjectActionPair[] = updates.filter(
    (update) => !relevantAccount.permissions.some((perm) => (perm.subject as keyof Subjects) === update.subject),
  );

  if (invalidUpdates.length > 0) {
    const invalidSubjects: string = invalidUpdates.map((u) => u.subject).join(', ');
    console.warn(`Account ${selectedCode} lacks permissions for subjects: ${invalidSubjects}`);
    return;
  }

  const newRules: RawRuleOf<AppAbility>[] = [];

  for (const permission of relevantAccount.permissions) {
    const action: Actions =
      updates.find((u) => (u.subject as keyof Subjects) === permission.subject)?.action || (permission.action as Actions);
    newRules.push(createPermissionRule(action, permission));
  }

  ability.update(newRules);
}

function addPermissionRule(
  can: (action: Actions, subject: Subjects, fields?: string[], conditions?: MongoQuery<Subjects>) => void,
  permission: { action: string; subject: string; fields?: string[]; conditions?: MongoQuery<Subjects> },
): void {
  const action = permission.action as Actions;
  const subject = permission.subject.toLowerCase() as keyof Subjects;
  const fields = permission.fields;
  const conditions = permission.conditions;

  can(action, subject, fields, conditions);
}

function createPermissionRule(
  action: Actions,
  permission: { subject: string; conditions?: MongoQuery<Subjects>; fields?: string[] },
): RawRuleOf<AppAbility> {
  return {
    action,
    subject: permission.subject as keyof Subjects,
    conditions: permission.conditions as MongoQuery<Subjects> | undefined,
    fields: permission.fields,
  };
}
